import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

interface CacheChangeKey {
  key: string;
  trackKey: string;
}

export type CacheKey = string | CacheChangeKey;

export const getKeyFromCacheKey = (cacheKey: CacheKey): string => {
  if (typeof cacheKey === 'string') {
    return cacheKey;
  }
  return cacheKey.key;
};

export const getTrackKeyFromCacheKey = (cacheKey: CacheKey): string | undefined => {
  if (typeof cacheKey === 'string') {
    return undefined;
  }
  return cacheKey.trackKey;
};

interface CacheContainer {
  obs$: Observable<any>;
  trackKey?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheContainer>();

  getCachedObservable<T>(cacheKey: CacheKey, fetchFunction: () => Observable<T>): Observable<T> {
    const key = getKeyFromCacheKey(cacheKey);
    const trackKey = getTrackKeyFromCacheKey(cacheKey);

    const cacheContainer = this.cache.get(key);

    // If trackId is given and it changes, we force refresh the cache
    if (trackKey && cacheContainer && cacheContainer.trackKey !== trackKey) {
      this.refreshCache(key);
    }

    if (!this.cache.has(key)) {
      const refreshKey = key;
      const data$ = fetchFunction().pipe(
        catchError(error => {
          // Handle errors if necessary
          this.refreshCache(refreshKey);
          return throwError(error);
        }),
        shareReplay(1)
      );
      this.cache.set(key, { obs$: data$ });
    }
    return this.cache.get(key)?.obs$ as Observable<T>;
  }

  refreshCache(key: string): void {
    if (this.cache.has(key)) {
      this.cache.get(key)?.obs$.subscribe().unsubscribe();
      this.cache.delete(key);
    }
  }
}
